import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button } from "../MuiGenerals";
import AddIcon from "@mui/icons-material/Add";
import { usePermission, useTranslation } from "../../hooks";
import { useZusTabsStore } from "../../zustand/store";
import { FeatureCodes } from "../../utils/constant";
import style from "./regulationDialogCss.module.css";
import MpTextFieldNumberOnly from "src/components/MpTextFieldNumberOnly";
import { useTranslate } from "react-admin";
import EnumWalletTypes from "@wallet-manager/node-types/dist/types/postgres/const/WalletType";
import {
  AddressBookFilterType,
  createAddressBookFilterConfig,
} from "src/utils/AddressBookFilter/createAddressBookFilter";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import PendingCount from "src/components/PendingCount";
import { initRegulationFields } from "./RenderDialog";
import { IRegulationObject, regulationItem } from "src/zustand/types";
import MultiselectAddressPicker from "../MultiselectAddressPicker";
import { AddressBookIconData } from "../AddressBookSuggestion";
import { useChains } from "../FetchConfig";
import { getWalletManagement } from "../../api/merchant";

const translatePrefix = "withdrawApprovalSetting";
const Pkey = FeatureCodes.assetManagement.WithdrawApprovalSetting.TabSetting;
const sx = {
  table: {
    backgroundColor: "#EAEAEA",
    px: 0.5,
    py: 1,
    my: 1,
  },
  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "> span:first-of-type": {
      flex: 1,
      display: "flex",
      "& span": {
        flex: 1,
      },
      "& span:first-of-type": {
        marginRight: "3.5rem",
      },
    },
    "& Button": {
      color: "black",
      backgroundColor: "white",
    },
  },
  tableContent: {
    px: 0.5,
    my: 1,
  },
  tableRow: {
    columnGap: "1rem",
    display: "flex",
    alignItems: "center",
    "& .MuiFormControl-root": { flex: 1 },
    my: 1,
  },
  numberInput: {
    backgroundColor: "white",
    width: "100%",
    height: "100%",
    margin: 0,
    "& .MuiInputBase-root": {
      flex: 1,
      alignItems: "center",
    },
    "& .MuiInputBase-input": {
      paddingTop: "12.5px",
      paddingBottom: "12.5px",
    },
  },
};
interface IRegulationItems {
  seq: number;
  approver_no: string;
  external_withdraw_amt: string;
  internal_withdraw_amt: string;
  swap_amt: string;
  sending_address?: string[];
}
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
type IDestinationTab = "external_withdraw" | "internal_withdraw" | "swap";
type TLocalProps = {
  regulationObj: IRegulationObject;
  setRegulationObj: (
    value: IRegulationObject,
  ) => void | React.Dispatch<React.SetStateAction<IRegulationObject>>;
  chainName?: string;
  mode?: "add" | "edit";
  /** Optional display metadata for the addresses in `sending_address`.
   *  Forwarded to MultiselectAddressPicker so chips render with `display_name`
   *  from the first paint. The caller is expected to recompute this when
   *  `chainName` changes (e.g. after fetching the metadata for the new chain). */
  metaMap?: Map<string, AddressBookIconData>;
};

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index} //hidden all if no index==value
      id={`simple-tabpanel-${index}`} //showing which tab
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      <Box>{children}</Box>
    </div>
  );
};
function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const TabView = (props: {
  destinationTab: IDestinationTab;
  regulationObj: IRegulationObject;
  setRegulationObj: (value: IRegulationObject) => void;
  chainName?: string;
  metaMap?: Map<string, AddressBookIconData>;
}) => {
  const {
    chainName,
    destinationTab,
    regulationObj,
    setRegulationObj,
    metaMap,
  } = props;
  const regulationType = destinationTab.replace(/_([a-z])/g, (g) =>
    g[1].toUpperCase(),
  ) as keyof IRegulationObject;
  const { hasPermission } = usePermission();
  const translate = useTranslate();
  const { t } = useTranslation(translatePrefix);
  const fieldName = `${destinationTab}_amt` as keyof IRegulationItems;

  const HOT_WALLET_FILTER_CONFIG = [
    createAddressBookFilterConfig(AddressBookFilterType.EOAHotWallet, {
      t: translate,
    }),
    createAddressBookFilterConfig(AddressBookFilterType.ContractHotWallet, {
      t: translate,
    }),
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const { value, id, name } = e.target;
    const regulationArrAfterUpdated = regulationObj[regulationType]!.map(
      (item) => {
        //{- -avoid nested object refer to old obj --}
        let newObj = { ...item };
        if (newObj.seq === Number(id)) {
          // newObj[`${name}`] = value;
          newObj = { ...newObj, [name]: value };
        }
        return newObj;
      },
    );

    setRegulationObj({
      ...regulationObj,
      [regulationType]: regulationArrAfterUpdated,
    });
  };
  const handleNumberInputEvent = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const { value } = e.target;

    if (value) {
      e.target.value = Number(value).toString();
    }

    handleInputChange(e);
  };

  const updateSendingAddress = (id: string, value: string[]) => {
    const regulationArrAfterUpdated = regulationObj[regulationType]!.map(
      (item) => {
        let newObj = { ...item };
        if (newObj.seq === Number(id)) {
          newObj = { ...newObj, sending_address: value };
        }
        return newObj;
      },
    );

    setRegulationObj({
      ...regulationObj,
      [regulationType]: regulationArrAfterUpdated,
    });
  };
  const handleDeleteItems = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    const { id } = e.target as HTMLInputElement;
    const remainingRegulationItems = regulationObj[regulationType]!.filter(
      (item) => item.seq !== Number(id),
    );

    setRegulationObj({
      ...regulationObj,
      [regulationType]: remainingRegulationItems,
    });
  };

  return (
    <Box>
      <Box sx={sx.tableContent}>
        <section
          className={`${style.gridHeaderContainer} ${
            destinationTab !== "internal_withdraw"
              ? style.externalAddressHeader
              : ""
          }`}
        >
          <div>{t("amountIslargerThan")}</div>
          <div>{t("noOfApprover")}</div>
          {destinationTab !== "internal_withdraw" && (
            <div>{t("sending_address")}</div>
          )}
        </section>
      </Box>

      {/* Regulation */}
      {regulationObj[regulationType]!.map((item, index) => (
        <Box key={index} sx={sx.table}>
          <section
            className={`${style.gridSection} ${
              destinationTab !== "internal_withdraw"
                ? style.externalAddressRow
                : ""
            }`}
          >
            <MpTextFieldNumberOnly
              name={fieldName}
              id={String(item.seq)}
              value={item[fieldName] ? item[fieldName] : ""}
              fullWidth
              sx={sx.numberInput}
              onChange={handleInputChange}
              disabled={!hasPermission(Pkey.EditRecord)}
            />
            <MpTextFieldNumberOnly
              name="approver_no"
              id={String(item.seq)}
              value={item.approver_no}
              fullWidth
              sx={sx.numberInput}
              integerOnly={true}
              onChange={handleNumberInputEvent}
              disabled={!hasPermission(Pkey.EditRecord)}
            />
            {destinationTab !== "internal_withdraw" && (
              <MultiselectAddressPicker
                value={item.sending_address!}
                onChange={(value: string[]) =>
                  updateSendingAddress(String(item.seq), value)
                }
                walletTypes={[String(EnumWalletTypes.HotWallet)]}
                filterConfigs={HOT_WALLET_FILTER_CONFIG}
                chain_name={chainName || ""}
                maxSelectable={5}
                placeholder={t("phSendingAddress")}
                metaMap={metaMap}
              />
            )}
            {(destinationTab === "swap" || item.seq !== 0) && (
              <div
                className={style.deleteBtn}
                style={{
                  gridColumn:
                    destinationTab !== "internal_withdraw" ? "4" : "3",
                }}
              >
                <Button
                  disabled={!hasPermission(Pkey.EditRecord)}
                  id={String(item.seq)}
                  sx={{ minWidth: 32 }}
                  onClick={handleDeleteItems}
                >
                  X
                </Button>
              </div>
            )}
          </section>
        </Box>
      ))}
      {destinationTab !== "internal_withdraw" && (
        <div
          style={{
            fontSize: 12,
            color: "#A2A2A2",
            marginTop: 8,
            marginBottom: 4,
          }}
        >
          {t("sending_address_description")}
        </div>
      )}
    </Box>
  );
};

const ApprovalRegulation = ({
  regulationObj,
  setRegulationObj,
  chainName,
  mode = "add",
  metaMap,
}: TLocalProps) => {
  const { t } = useTranslation(translatePrefix);
  const { hasPermission } = usePermission();
  const [tabsIndex, setTabsIndex] = useState(0);
  const zusTabsStore = useZusTabsStore((state) => state);
  const { selectChainByPieces } = useChains();
  const [seedMetaMap, setSeedMetaMap] = useState<
    Map<string, AddressBookIconData>
  >(() => new Map());

  useEffect(() => {
    if (mode === "edit") return;

    const resetRegulationFields = (
      items: IRegulationObject["externalWithdraw" | "swap"],
    ) =>
      items.map((item) => ({
        ...item,
        sending_address: [],
      }));

    setRegulationObj({
      ...regulationObj,
      externalWithdraw: resetRegulationFields(regulationObj.externalWithdraw),
      swap: resetRegulationFields(regulationObj.swap),
    });
  }, [chainName]);

  // Fetch display metadata for the addresses currently in `sending_address`
  // when the chain changes. In edit mode the record already carries addresses
  // for the bound chain, so we resolve them on first render.
  useEffect(() => {
    if (mode === "add") return;
    const addresses = [
      ...regulationObj.externalWithdraw.flatMap(
        (item) => item.sending_address ?? [],
      ),
      ...regulationObj.swap.flatMap((item) => item.sending_address ?? []),
    ];
    const unique = Array.from(new Set(addresses)).filter(Boolean);
    if (!chainName || unique.length === 0) {
      setSeedMetaMap(new Map());
      return;
    }

    const { chain_type, chain_id } = selectChainByPieces({
      chain_name: chainName,
    });
    if (!chain_type || !chain_id) {
      setSeedMetaMap(new Map());
      return;
    }

    let cancelled = false;
    (async () => {
      const res = await getWalletManagement({
        chain_type,
        chain_id,
        statuses: [1],
        wallet_types: [String(EnumWalletTypes.HotWallet)],
        walletAddress: unique,
        pageSize: unique.length || 50,
        page: 0,
      });
      if (cancelled || !res?.rows) return;
      const next = new Map<string, AddressBookIconData>();
      for (const row of res.rows) {
        if (!row?.wallet_address || !unique.includes(row.wallet_address)) {
          continue;
        }
        next.set(row.wallet_address, {
          display_name: row.display_name,
          wallet_type: row.wallet_type,
          smart_account_type: row.smart_account_type,
        });
      }
      setSeedMetaMap(next);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainName, selectChainByPieces]);

  const effectiveMetaMap = useMemo(() => {
    if (!metaMap && seedMetaMap.size === 0) return undefined;
    const merged = new Map<string, AddressBookIconData>();
    seedMetaMap.forEach((v, k) => merged.set(k, v));
    if (metaMap) {
      metaMap.forEach((v, k) => merged.set(k, v));
    }
    return merged;
  }, [metaMap, seedMetaMap]);

  // A record "has value" when the user has filled in any amount, approver, or
  // sending address — empty placeholder rows (just added, not yet edited) are
  // excluded so the tab badge reflects actual configured regulations.
  const hasValue = (item: regulationItem): boolean => {
    const textFields: (keyof regulationItem)[] = [
      "approver_no",
      "external_withdraw_amt",
      "internal_withdraw_amt",
      "swap_amt",
    ];
    const hasTextValue = textFields.some(
      (field) =>
        typeof item[field] === "string" &&
        (item[field] as string).trim() !== "",
    );
    const hasAddress =
      Array.isArray(item.sending_address) && item.sending_address.length > 0;
    return hasTextValue || hasAddress;
  };

  const destinationTabs = [
    {
      name: "external_withdraw",
      regulationType: "externalWithdraw",
      jsxNode: (
        <TabView
          destinationTab={"external_withdraw"}
          regulationObj={regulationObj}
          setRegulationObj={setRegulationObj}
          chainName={chainName}
          metaMap={effectiveMetaMap}
        />
      ),
      countDom: (
        <PendingCount
          count={regulationObj.externalWithdraw.filter(hasValue).length}
        />
      ),
    },
    {
      name: "internal_withdraw",
      regulationType: "internalWithdraw",
      jsxNode: (
        <TabView
          destinationTab={"internal_withdraw"}
          regulationObj={regulationObj}
          setRegulationObj={setRegulationObj}
          chainName={chainName}
          metaMap={effectiveMetaMap}
        />
      ),
      countDom: (
        <PendingCount
          count={regulationObj.internalWithdraw.filter(hasValue).length}
        />
      ),
    },
    {
      name: "swap",
      regulationType: "swap",
      jsxNode: (
        <TabView
          destinationTab={"swap"}
          regulationObj={regulationObj}
          setRegulationObj={setRegulationObj}
          chainName={chainName}
          metaMap={effectiveMetaMap}
        />
      ),
      countDom: (
        <PendingCount count={regulationObj.swap.filter(hasValue).length} />
      ),
    },
  ];

  const handleTabChange = (event: React.SyntheticEvent, tabsEnum: number) => {
    zusTabsStore.setGridTabsEumn(0);
    setTabsIndex(tabsEnum);
  };

  const regulationType = destinationTabs[tabsIndex]
    .regulationType as keyof IRegulationObject;
  const currentId = regulationObj[regulationType]
    .map((item) => item.seq)
    .sort((a, b) => a - b)
    .slice(-1)[0];

  //{- -Handle Functions - -}
  const handleAddItems = () => {
    if (regulationObj[regulationType].length === 0) {
      const firstRegulationArr = [
        {
          ...initRegulationFields,
        },
      ];
      setRegulationObj({
        ...regulationObj,
        [regulationType]: firstRegulationArr,
      });
      return;
    }
    //{- -default regulationItem Format with s only - -}
    const newItem: IRegulationItems[] = [
      {
        ...initRegulationFields,
        seq: currentId + 1,
      },
    ];
    const sumOfRegulationsArr = [...regulationObj[regulationType], ...newItem];
    setRegulationObj({
      ...regulationObj,
      [regulationType]: sumOfRegulationsArr,
    });
  };
  const hasEditPermission = hasPermission(
    mode === "add" ? Pkey.AddRecord : Pkey.EditRecord,
  );
  return (
    <>
      <Box sx={sx.tableHeader}>
        <span>
          <span style={{ fontWeight: "bold" }}>{t("regulation")}</span>
        </span>
        {hasEditPermission && (
          <Button
            onClick={handleAddItems}
            startIcon={<AddIcon />}
            sx={{ fontFamily: ["Roboto", "Helvetica", "Arial", "sans-serif"] }}
          >
            {t("add_item")}
          </Button>
        )}
      </Box>
      <Box>
        <Tabs
          sx={{ borderBottom: "solid #E1E4EA 1px" }}
          value={tabsIndex}
          onChange={handleTabChange}
        >
          {destinationTabs.map((item, index) => {
            const name = (
              <Box
                sx={{ display: "flex", gap: "0.2rem", alignItems: "center" }}
              >
                {t(item.name)}
                {item.countDom}
              </Box>
            );

            return <Tab key={index} label={name} {...a11yProps(index)} />;
          })}
        </Tabs>
        {/* {- - Real Tab Content- - -} */}
        {destinationTabs.map((item, index) => {
          return (
            <TabPanel key={index} value={tabsIndex} index={index}>
              {item.jsxNode}
            </TabPanel>
          );
        })}
      </Box>
    </>
  );
};

export default ApprovalRegulation;
